import type { Page } from '@getaccept/dsr-shared';
import type { DsrPublished } from '@getaccept/dsr-shared/src/types/dsr';
import type { LoadImagePayload } from '@getaccept/editor-lib';
import { getLoadImageURLPayloads, setImageSrcURL } from '@getaccept/editor-lib';
import { ImageHeight } from '@getaccept/lib-shared/src/enums/image-height';
import type { SigningTheme } from '@getaccept/lib-shared/src/signing-theme/types/theme';
import type { ComputedRef, Ref } from '@vue/composition-api';
import { computed, ref } from '@vue/composition-api';
import { DocumentRevisionStatus } from '@getaccept/lib-shared/src/types/document-revision';
import type { DocumentRecipientRevision } from '@getaccept/lib-shared/src/types/document-revision';
import { DsrService } from '../../api/dsr/service/dsr.service';
import { useRevisionsStore } from '../store/revisions.store';

export function useLoadDSR() {
  const revisionsStore = useRevisionsStore();

  const loading: Ref<boolean> = ref(true);
  const error: Ref<boolean> = ref(false);
  const dsr: Ref<DsrPublished> = ref(null);

  const pages: Ref<Page[]> = ref([]);
  const currentPage: Ref<Page> = ref(null);
  const currentPageId: ComputedRef<string> = computed(() => currentPage.value?.id || '');

  const documentRevisions: Ref<DocumentRecipientRevision[]> = ref([]);

  const loadDSR = async () => {
    try {
      [dsr.value, documentRevisions.value, pages.value] = await Promise.all([
        DsrService.load(),
        DsrService.loadRevisions(),
        DsrService.loadPages(),
      ]);

      initialize();
    } catch (e) {
      error.value = true;
      console.error('Could not load DSR', e);
    } finally {
      loading.value = false;
      loadEditorImageURLs();
    }
  };

  const loadNewPages = async () => {
    try {
      pages.value = await DsrService.loadPages();
      documentRevisions.value = revisionsStore.revisions;
      initialize();
    } catch (e) {
      error.value = true;
      console.error('Could not load new content', e);
    } finally {
      loading.value = false;
      loadEditorImageURLs();
    }
  };

  const initialize = () => {
    const firstVisiblePage = pages.value.find(
      ({ editorBlock }) => !editorBlock?.content?.sections?.every(({ hidden }) => hidden)
    );
    currentPage.value = pages.value?.length ? firstVisiblePage : null;

    revisionsStore.setRevisions(documentRevisions.value);
    revisionsStore.setRevision(documentRevisions.value[0].revision);
    setViewed();
  };

  const setViewed = () => {
    const latestSealedRevision = documentRevisions.value
      .filter(revision => revision.status === DocumentRevisionStatus.Sealed)
      .reverse()[0];

    if (!latestSealedRevision || latestSealedRevision.viewedByRecipient) {
      return;
    }

    revisionsStore.setRevisionViewed(latestSealedRevision.revision);
  };

  const loadEditorImageURLs = () => {
    const loadImagePayloads = (pages.value || [])
      .filter(({ editorBlock }) => editorBlock)
      .flatMap(({ editorBlock }) => getLoadImageURLPayloads(editorBlock));

    loadImagePayloads.forEach(loadEditorImageURL);
  };

  const loadEditorImageURL = async (loadImagePayload: LoadImagePayload) => {
    try {
      const { url: srcUrl, externalId } = await DsrService.getEditorImageURL(
        loadImagePayload.imageId,
        ImageHeight.QHD
      );

      pages.value = setImageSrcURL(
        { pages: pages.value },
        {
          ...loadImagePayload,
          srcUrl,
          externalId,
        }
      )?.pages as Page[];

      setCurrentPage(currentPage.value.id);
    } catch (err) {
      console.error(err);
    }
  };

  const theme: Ref<SigningTheme> = ref(null);

  const loadTheme = async (documentId: string) => {
    try {
      theme.value = await DsrService.loadTheme(documentId);
    } catch (e) {
      console.error(e);
    }
  };

  const loadRoomSettings = async () => {
    try {
      const settings = await DsrService.loadRoomSettings();
      dsr.value = {
        ...dsr.value,
        settings,
      };
    } catch (e) {
      console.error(e);
    }
  };

  const setCurrentPage = (id: string) =>
    (currentPage.value = (pages.value || []).find((page: Page) => page.id === id));

  return {
    setCurrentPage,
    currentPage,
    currentPageId,
    loading,
    pages,
    documentRevisions,
    dsr,
    error,
    theme,
    loadDSR,
    loadNewPages,
    loadRoomSettings,
    loadTheme,
    loadEditorImageURLs,
    loadEditorImageURL,
    setViewed,
  };
}
